import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useEffectEvent } from "react";
import { toast } from "sonner";

import { InvitationDetails } from "@/dashboard/invitation-details";
import { useCurrentTime } from "@/hooks/use-current-time";
import { useUserInfo } from "@/hooks/users/use-user-info";
import { useTRPC } from "@/infrastructure/trpc/react";
import { PublicLayout } from "@/shared/layout/public-layout";
import { Button } from "@/shared/ui/button";

export const Route = createFileRoute("/invitations/$id")({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.organizationInvitations.getInvitationDetails.queryOptions(
          {
            invitationId: params.id,
          }
        )
      )
      .catch((error) => {
        console.warn("Failed to prefetch invitation", {
          error,
          invitationId: params.id,
        });
      });
  },
  component: RouteComponent,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function RouteComponent() {
  const now = useCurrentTime();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // Get current user info
  const { data: userInfo } = useUserInfo();

  const {
    data: invitation,
    isLoading,
    error,
  } = useQuery(
    trpc.organizationInvitations.getInvitationDetails.queryOptions(
      {
        invitationId: id,
      }
    )
  );

  // Effect event for navigation - doesn't need to trigger effect re-runs
  const onValidInvitation = useEffectEvent(() => {
    void navigate({ to: "/app/dashboard" });
  });

  // If user is logged in and invitation is valid, redirect to dashboard
  useEffect(() => {
    if (userInfo && invitation && !error) {
      const userEmail = userInfo.email;
      const isCorrectUser = userEmail === invitation.email;
      const isExpired =
        new Date(invitation.expiresAt).getTime() <
        Date.now();

      // Only redirect if user email matches and invitation is not expired
      if (isCorrectUser && !isExpired) {
        // User will see the invitation modal on dashboard
        onValidInvitation();
      }
    }
  }, [userInfo, invitation, error]);

  const acceptInvitation = useMutation(
    trpc.organizationInvitations.acceptInvitation.mutationOptions(
      {
        onSuccess: async (data) => {
          toast.success(
            "Successfully joined the organization!"
          );
          // Invalidate organization queries to refresh sidebar
          await queryClient.invalidateQueries(
            trpc.organization.getOrganizationsDetails.queryOptions()
          );
          void navigate({
            to: "/app/organization/$id",
            params: { id: data.organizationId },
          });
        },
        onError: (error) => {
          toast.error(
            error.message || "Failed to accept invitation"
          );
        },
      }
    )
  );

  const handleAcceptInvitation = () => {
    if (!userInfo) {
      // User needs to be logged in to accept invitation
      toast.error(
        "Please log in to accept this invitation"
      );
      return;
    }

    acceptInvitation.mutate({ invitationId: id });
  };

  const handleDeclineInvitation = () => {
    void navigate({ to: "/app/dashboard" });
  };

  if (isLoading) {
    return (
      <PublicLayout
        title="Invitation"
        subtitle="Loading Invitation..."
        description="Loading invitation details..."
      >
        <div className="text-center">
          <p className="text-muted-foreground">
            Loading invitation...
          </p>
        </div>
      </PublicLayout>
    );
  }

  // Handle already processed invitations (accepted or dismissed)
  if (invitation && invitation.status !== "pending") {
    const isAccepted = invitation.status === "accepted";

    return (
      <PublicLayout
        title="Invitation"
        subtitle={
          isAccepted
            ? "Invitation Already Accepted"
            : "Invitation Declined"
        }
        description={
          isAccepted
            ? "This invitation has already been accepted."
            : "This invitation has been declined."
        }
      >
        <div className="text-center">
          <p className="mb-4 text-muted-foreground">
            {isAccepted
              ? "You have already joined this organization."
              : "This invitation was declined."}
          </p>
          <Button
            onClick={() =>
              void navigate({ to: "/app/dashboard" })
            }
          >
            Go to Dashboard
          </Button>
        </div>
      </PublicLayout>
    );
  }

  if (error || !invitation) {
    return (
      <PublicLayout
        title="Invitation"
        subtitle="Invitation Not Found"
        description="This invitation link is invalid or has expired."
      >
        <div className="text-center">
          <p className="mb-4 text-muted-foreground">
            The invitation you're looking for doesn't exist
            or has expired.
          </p>
          <Button
            onClick={() => void navigate({ to: "/" })}
          >
            Go to Home
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const isExpired =
    new Date(invitation.expiresAt).getTime() < now;
  const userEmail = userInfo?.email;
  const isCorrectUser = userEmail === invitation.email;
  const hasAccount = !!userInfo;

  return (
    <PublicLayout title="Invitation">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-lg border bg-card p-6">
          <InvitationDetails
            invitation={invitation}
            onAccept={handleAcceptInvitation}
            onDecline={handleDeclineInvitation}
            isAccepting={acceptInvitation.isPending}
            showExpired={true}
            actionButtons={
              !hasAccount ? (
                <div className="space-y-4">
                  <div className="rounded-md bg-muted p-4">
                    <p className="text-sm">
                      You need to sign in with your Google
                      account to accept this invitation.
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      void navigate({ to: "/sign-in" });
                    }}
                    className="w-full"
                  >
                    Sign In with Google
                  </Button>
                </div>
              ) : isExpired ? (
                <div className="rounded-md bg-destructive/10 p-4 text-destructive">
                  <p className="text-sm font-medium">
                    This invitation has expired
                  </p>
                  <p className="mt-1 text-sm">
                    Please contact the organization admin
                    for a new invitation.
                  </p>
                </div>
              ) : !isCorrectUser ? (
                <div className="rounded-md bg-warning/10 p-4 text-warning-foreground">
                  <p className="text-sm font-medium">
                    This invitation was sent to a different
                    email
                  </p>
                  <p className="mt-1 text-sm">
                    You're signed in as {userEmail}, but
                    this invitation was sent to{" "}
                    {invitation.email}.
                  </p>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDeclineInvitation}
                    disabled={acceptInvitation.isPending}
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={handleAcceptInvitation}
                    disabled={acceptInvitation.isPending}
                  >
                    {acceptInvitation.isPending
                      ? "Accepting..."
                      : "Accept Invitation"}
                  </Button>
                </div>
              )
            }
          />
        </div>
      </div>
    </PublicLayout>
  );
}
